// components/TechGlobe.js
import React, { useEffect, useRef } from 'react';
import './TechGlobe.css';

const TechGlobe = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  const RADIUS = 180; // Globe radius
  const DOTS_COUNT = 100;
  const MAX_CONNECTION_DIST = 90;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let width = canvas.width;
    let height = canvas.height;

    // Handle scaling for high-DPI displays (retina screens)
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      width = rect.width;
      height = rect.height;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Generate dots in a spherical coordinates configuration
    const generateDots = () => {
      const dots = [];
      for (let i = 0; i < DOTS_COUNT; i++) {
        // Spherical distribution
        const phi = Math.acos(2 * Math.random() - 1);
        const theta = 2 * Math.PI * Math.random();

        // Convert to Cartesian 3D coordinates
        const x3d = RADIUS * Math.sin(phi) * Math.cos(theta);
        const y3d = RADIUS * Math.sin(phi) * Math.sin(theta);
        const z3d = RADIUS * Math.cos(phi);

        dots.push({
          x3d,
          y3d,
          z3d,
          x: 0, // Projected 2D coordinates
          y: 0,
          size: Math.random() < 0.2 ? 2.5 : Math.random() < 0.6 ? 2 : 1.5,
          speedFactor: 0.3 + Math.random() * 0.5,
          movementRadius: 2 + Math.random() * 3,
          phase: Math.random() * Math.PI * 2
        });
      }
      return dots;
    };

    const dots = generateDots();
    let rotationAngle = 0;
    const rotationSpeed = 0.003; // Slow, smooth rotation speed (radians per frame)

    const animate = (timestamp) => {
      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Center context
      const centerX = width / 2;
      const centerY = height / 2;

      rotationAngle += rotationSpeed;

      const cosRot = Math.cos(rotationAngle);
      const sinRot = Math.sin(rotationAngle);

      // 1. Calculate projected 2D coordinates with 3D rotation and local oscillations
      const time = timestamp * 0.001; // Seconds

      dots.forEach(dot => {
        // Rotate in 3D space around Y axis
        const rotX = dot.x3d * cosRot - dot.z3d * sinRot;
        const rotZ = dot.x3d * sinRot + dot.z3d * cosRot;
        const rotY = dot.y3d;

        // Apply local oscillating movement (energy vibe)
        const offsetX = Math.cos(time * dot.speedFactor + dot.phase) * dot.movementRadius;
        const offsetY = Math.sin(time * dot.speedFactor + dot.phase * 1.3) * dot.movementRadius;

        // 3D to 2D projection with perspective depth factor
        const perspective = 300 / (300 + rotZ); // Depth scale factor
        dot.x = centerX + (rotX + offsetX) * perspective;
        dot.y = centerY + (rotY + offsetY) * perspective;
        dot.depth = rotZ; // Store depth for z-sorting if needed
      });

      // 2. Draw Connections
      ctx.lineWidth = 1;
      for (let i = 0; i < dots.length; i++) {
        const dot1 = dots[i];
        for (let j = i + 1; j < dots.length; j++) {
          const dot2 = dots[j];

          const dx = dot1.x - dot2.x;
          const dy = dot1.y - dot2.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < MAX_CONNECTION_DIST) {
            const opacity = (1 - distance / MAX_CONNECTION_DIST) * 0.25;
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.beginPath();
            ctx.moveTo(dot1.x, dot1.y);
            ctx.lineTo(dot2.x, dot2.y);
            ctx.stroke();
          }
        }
      }

      // 3. Draw Dots
      dots.forEach(dot => {
        // Fade dots in background for depth illusion
        const opacity = Math.max(0.2, (RADIUS - dot.depth) / (2 * RADIUS));
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.size, 0, 2 * Math.PI);
        ctx.fill();

        // Subtle glow for larger dots
        if (dot.size > 2) {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity * 0.3})`;
          ctx.beginPath();
          ctx.arc(dot.x, dot.y, dot.size * 2, 0, 2 * Math.PI);
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div className="network-container">
      <div className="network">
        <canvas ref={canvasRef} className="network-canvas" />
      </div>
    </div>
  );
};

export default TechGlobe;